import type { CardSlotUiView } from '@kenocyber/game-domain';
import type { CardSlot } from '@kenocyber/shared';
import { BleedButtonLabel } from '../game/bleed-button-label';
import { CardHudBar } from '../ui/CardHudBar';
import type { PvpHealthBar, PvpHudView } from './hud';

/**
 * The duel's combat interface.
 *
 * Presentation only: it is handed a view model and renders it. There is no
 * frame, no seat arithmetic and no result logic here - all of that is
 * `pvpHudView`, which is proven without a renderer.
 *
 * ## It sits AROUND the arena, never over it
 *
 * The two health bars go to the top corners and the clock between them, along
 * the very top of the play area; the help line goes along the bottom. Nothing
 * is placed in the middle, because the middle is where two ships are shooting
 * at each other.
 */

export interface PvpHudProps {
  readonly view: PvpHudView;
  /**
   * Opens the arena's target mode.
   *
   * Optional, because the HUD is also rendered in states where there is no
   * arena to arm - and because a button that did nothing would be worse than
   * a read-out. When it is absent the teleport badge is a label like the rest.
   */
  readonly onArmTeleport?: () => void;
  /**
   * Manda rebentar as marcas de sangramento (gate V28G.N).
   *
   * Opcional pela mesma razão que {@link PvpHudProps.onArmTeleport} é: o HUD
   * também é desenhado em estados onde não há arena a quem dar ordens. Sem ele,
   * o botão não aparece de todo - um botão que não faz nada seria pior do que a
   * sua ausência.
   */
  readonly onDetonateBleed?: () => void;
  /**
   * Os três espaços de cartas DESTA cadeira (gate V28H.J).
   *
   * Vazio enquanto não há partida, que é o estado em que o HUD também é
   * desenhado. A barra desenha-se na mesma, com três caixas vazias, porque um
   * jogador que não tenha cartas continua a ver onde elas iriam.
   */
  readonly cardSlots?: readonly CardSlotUiView[];
  /**
   * A ORDEM de ligar uma carta. Ausente quando não há arena a quem a dar.
   *
   * A mesma disciplina de {@link PvpHudProps.onDetonateBleed}: sem ela a barra
   * desenha-se e não aceita ordem nenhuma, em vez de aceitar uma que não chega
   * a lado nenhum.
   */
  readonly onActivateCard?: (slot: CardSlot) => void;
  /** E a soltura da carga, quando o dedo sai (gate V28H.N). */
  readonly onReleaseCard?: ((slot: CardSlot) => void) | undefined;
  /**
   * **Está encostado a um Leviatã vazio?** (gate V28H.X).
   *
   * A cena responde, porque é ela que tem as posições desenhadas. O HUD só
   * decide se mostra o botão.
   */
  readonly canBoard?: boolean | undefined;
  /** E a ORDEM de pilotar. Ausente quando não há arena a quem a dar. */
  readonly onBoard?: (() => void) | undefined;
}

export function PvpHud({
  view,
  onArmTeleport,
  onDetonateBleed,
  cardSlots,
  onActivateCard,
  onReleaseCard,
  canBoard,
  onBoard,
}: PvpHudProps) {
  return (
    <div className="pvp-hud" data-testid="pvp-hud">
      {/*
       * **A barra das três cartas, no canto inferior esquerdo** (gate V28H.J).
       *
       * O MESMO componente da caça, com a mesma folha de estilo e a mesma
       * ancoragem - o dono aprovou-a lá, e um duelo com uma barra diferente
       * seria o jogador a aprender duas interfaces para a mesma coisa.
       *
       * `enabled` é falso sem arena a quem dar ordens: a barra desenha-se, e as
       * teclas ficam mudas.
       */}
      <CardHudBar
        slots={cardSlots ?? []}
        enabled={onActivateCard !== undefined}
        onActivate={(slot) => onActivateCard?.(slot)}
        onRelease={(slot) => onReleaseCard?.(slot)}
      />
      {/*
       * **O botão de PILOTAR** (gate V28H.X).
       *
       * Só existe enquanto houver uma estrutura encostada e vazia: um botão
       * permanente que quase nunca faz nada é ruído, e este aparece exactamente
       * quando pode ser carregado.
       *
       * Ele MANDA a ordem e não embarca ninguém - a sala é que verifica se o
       * casco continua encostado no tique em que ela chega.
       */}
      {canBoard === true && onBoard !== undefined ? (
        <button
          type="button"
          className="pvp-hud-board"
          data-testid="pvp-board-button"
          onClick={() => onBoard()}
        >
          PILOTAR
        </button>
      ) : null}
      <div className="pvp-hud-top">
        <PvpFighter label="VOCÊ" side="you" bar={view.you} />

        <div className="pvp-hud-centre">
          {view.timeLabel ? (
            <span className="pvp-hud-clock" data-testid="pvp-hud-time">
              {view.timeLabel}
            </span>
          ) : null}
          <span className="pvp-hud-phase" data-testid="pvp-hud-phase">
            {view.phaseLabel}
          </span>
          {/* Said where the fight is, not only in the lobby. */}
          <span className="pvp-hud-no-reward" data-testid="pvp-hud-no-reward">
            {view.noRewardNotice}
          </span>
          {/*
           * Suppressed while reconnecting, because the alert below says the
           * same word louder. The screenshot had "Reconectando" three times in
           * a 100px column, and a screen reader read the status and the alert
           * one after the other.
           */}
          {view.reconnecting ? null : (
            <span
              className={`pvp-hud-connection tone-${view.connection}`}
              data-testid="pvp-hud-connection"
              /*
               * Announced, because a player who cannot see the corner of the
               * screen still needs to know the socket went away.
               */
              role="status"
              aria-live="polite"
            >
              {view.connectionLabel}
            </span>
          )}
        </div>

        <PvpFighter label="ADVERSÁRIO" side="them" bar={view.them} />
      </div>

      {view.prepareLabel ? (
        <p className="pvp-hud-prepare" data-testid="pvp-hud-prepare" role="status">
          {view.prepareLabel}
        </p>
      ) : null}

      {view.reconnecting ? (
        /*
         * Over the arena, which stays exactly as the last authoritative frame
         * left it. Nothing moves underneath: no invented movement, no invented
         * shot, and above all no invented defeat.
         */
        <div className="pvp-hud-reconnecting" data-testid="pvp-hud-reconnecting" role="alert">
          RECONECTANDO…
        </div>
      ) : null}

      {view.effects.length > 0 ? (
        <ul className="pvp-hud-effects" data-testid="pvp-hud-effects">
          {view.effects.map((effect) => (
            <li
              key={effect.kind}
              className={`pvp-hud-effect pvp-hud-effect-${effect.kind}`}
              data-testid={`pvp-hud-effect-${effect.kind}`}
            >
              {/*
               * A charge is the one effect a player DOES something with, so
               * it is the one that is a button. The others are read-outs: a
               * player cannot choose to stop having Double Damage.
               *
               * It only opens the target mode. Nothing is spent here, the
               * room hears nothing, and the same button is a no-op if the
               * charge is gone by the time it is pressed.
               */}
              {effect.kind === 'teleport' && onArmTeleport ? (
                <button
                  type="button"
                  className="pvp-hud-effect-arm"
                  data-testid="pvp-teleport-arm"
                  onClick={onArmTeleport}
                >
                  {effect.label}
                </button>
              ) : (
                <span className="pvp-hud-effect-label">{effect.label}</span>
              )}
              {/*
               * Seconds for a timer, a count for a charge. A charge does not
               * expire, and printing a made-up duration beside it would be
               * telling the player a rule the room does not have.
               */}
              <span className="pvp-hud-effect-value">
                {effect.secondsLeft === null ? `x${effect.count}` : `${effect.secondsLeft}s`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/*
       * **DETONAR** (gate V28G.N).
       *
       * Um botão PRÓPRIO, e não um crachá de efeito: os crachás são leituras -
       * um jogador não escolhe deixar de ter Dano Dobrado - e isto é uma ação de
       * combate. Fica ao lado deles e não sobre a arena, pela mesma regra que
       * põe tudo o resto à volta do campo.
       *
       * Não dispara o canhão, não substitui controlo nenhum e não usa a soltura
       * do rato. Tem tecla - o `1`, pedido pelo dono depois do primeiro ensaio -
       * e ela estava LIVRE: o §4 proíbe roubar uma já ocupada, e nenhum controlo
       * desta arena usava os algarismos.
       *
       * `disabled` sai da autoridade - sem marcas, ou morto - e o total
       * impresso é o mesmo número que a ordem vai rebentar.
       */}
      {onDetonateBleed ? (
        <button
          type="button"
          className="pvp-hud-detonate"
          data-testid="pvp-detonate"
          disabled={!view.canDetonate}
          onClick={onDetonateBleed}
        >
          <BleedButtonLabel marks={view.bleedMarks} />
        </button>
      ) : null}

      {view.controlsEnabled ? (
        <p className="pvp-hud-help" data-testid="pvp-hud-help">
          <span>WASD — mover</span>
          <span>Espaço — teleporte</span>
          <span>Mouse — mirar</span>
          <span>Clique — atirar</span>
          <span>Scroll — zoom</span>
        </p>
      ) : null}
    </div>
  );
}

function PvpFighter({
  label,
  side,
  bar,
}: {
  readonly label: string;
  readonly side: 'you' | 'them';
  readonly bar: PvpHealthBar | null;
}) {
  if (!bar) {
    return <div className={`pvp-fighter pvp-fighter-${side}`} />;
  }
  return (
    <div className={`pvp-fighter pvp-fighter-${side}`} data-testid={`pvp-fighter-${side}`}>
      <span className="pvp-fighter-label">{label}</span>
      <div
        className="pvp-fighter-track"
        /*
         * A real progressbar rather than a styled div: a screen reader has no
         * other way to read a bar, and the numbers below are small on purpose.
         */
        role="progressbar"
        aria-label={`Vida — ${label}`}
        /*
         * A razão EXATA, em milli, para quem lê com um leitor de ecrã.
         *
         * `aria-valuenow` tem de ser um número, e um milésimo cabe num `double`
         * para qualquer vida jogável - mas o TEXTO ao lado é o formatador
         * canónico, e é ele que diz `108,12` em vez de `108120`.
         */
        aria-valuenow={Number(bar.currentMilli)}
        aria-valuemin={0}
        aria-valuemax={Number(bar.maxMilli)}
        aria-valuetext={`${bar.currentText} de ${bar.maxText}`}
        data-testid={`pvp-health-${side}`}
      >
        {/*
         * Width set inline because it is DATA, not style: it is the server's
         * number, and a stylesheet cannot hold a value that changes every tick.
         * No transition - damage is discrete, and animating it would draw a
         * value the room never reported.
         */}
        <div className="pvp-fighter-fill" style={{ width: `${bar.percent}%` }} />
      </div>
      <span className="pvp-fighter-value">
        {bar.currentText} / {bar.maxText}
      </span>
    </div>
  );
}
